<!--
  @component ActivityFeed — Phase 4 account-wide security audit trail.
  tier:   deployments section (scope-independent — the audit ledger is a
          per-actor, account-wide record, not a per-project view)

  The tamper-evident record of every privileged operation: what ran, allowed
  or blocked, when, and under which profile. Sourced from security.audit.tail
  (real, durable) with a chain-integrity badge from security.audit.verify.
  Distinct from Logs (the deploy/event stream) — this is the authorization
  ledger. Real data — no fabrication.
  reads: useDeploymentIO (security.audit.tail / security.audit.verify)
-->
<script lang="ts">
  import { useDeploymentIO } from "../../../../global/io/nodes/deployment/DeploymentIO.svelte";
  import { relTime } from "../../home/home-util.js";

  const node = useDeploymentIO();

  interface AuditRow { op: string; status: "success" | "failure"; atMs: number; profileId: string; reason?: string; }
  type VerifyBadge = "unknown" | "verifying" | "intact" | "tampered" | "error";

  let rows = $state<AuditRow[]>([]);
  let loaded = $state(false);
  let err = $state<string | null>(null);
  let badge = $state<VerifyBadge>("unknown");
  let count = $state<number | null>(null);
  let filter = $state<"all" | "failure">("all");

  function coerce(raw: unknown): AuditRow {
    const x = (typeof raw === "object" && raw !== null ? raw : {}) as Record<string, unknown>;
    return {
      op: typeof x["op"] === "string" ? x["op"] : "",
      status: x["status"] === "failure" ? "failure" : "success",
      atMs: typeof x["atMs"] === "number" ? x["atMs"] : 0,
      profileId: typeof x["profileId"] === "string" ? x["profileId"] : "",
      ...(typeof x["reason"] === "string" ? { reason: x["reason"] } : {}),
    };
  }

  async function load(): Promise<void> {
    try {
      const r = (await node.dispatch("security.audit.tail", { limit: 250 })) as { entries?: unknown };
      const list = Array.isArray(r?.entries) ? (r.entries as unknown[]).map(coerce) : [];
      rows = list.reverse(); // server returns oldest→newest; show newest first
      err = null;
    } catch (e) {
      rows = []; err = e instanceof Error ? e.message : String(e);
    } finally { loaded = true; }
  }
  async function verify(): Promise<void> {
    badge = "verifying";
    try {
      const r = (await node.dispatch("security.audit.verify")) as { ok?: boolean; count?: number };
      badge = r?.ok === true ? "intact" : "tampered";
      count = typeof r?.count === "number" ? r.count : null;
    } catch { badge = "error"; }
  }
  $effect(() => { void load(); void verify(); });

  const shown = $derived(filter === "all" ? rows : rows.filter((r) => r.status === "failure"));
  const failures = $derived(rows.filter((r) => r.status === "failure").length);

  const BADGE_LABEL: Record<VerifyBadge, string> = {
    unknown: "—", verifying: "verifying…", intact: "chain intact", tampered: "tampered", error: "verify failed",
  };
</script>

<div class="activity">
  <header>
    <div class="title">
      <h1>Activity</h1>
      <p class="lede">The tamper-evident audit trail — every privileged operation, allowed or blocked, across your account. Newest first.</p>
    </div>
    <div class="tools">
      <button type="button" class="badge {badge}" onclick={() => void verify()} title="Re-verify the durable audit chain">
        <span class="dot"></span>{BADGE_LABEL[badge]}{#if count !== null} · {count}{/if}
      </button>
      <div class="seg" role="tablist" aria-label="Filter">
        <button type="button" role="tab" aria-selected={filter === "all"} class:on={filter === "all"} onclick={() => (filter = "all")}>All</button>
        <button type="button" role="tab" aria-selected={filter === "failure"} class:on={filter === "failure"} onclick={() => (filter = "failure")}>Blocked{failures ? ` · ${failures}` : ""}</button>
      </div>
    </div>
  </header>

  {#if err}<p class="err">{err}</p>{/if}

  {#if !loaded}
    <p class="muted">Loading audit trail…</p>
  {:else if shown.length === 0}
    <p class="muted">{filter === "failure" ? "No blocked operations — clean." : "No audit entries yet."}</p>
  {:else}
    <ul class="trail">
      {#each shown as e, i (i + ":" + e.atMs + ":" + e.op)}
        <li class="line" class:fail={e.status === "failure"}>
          <span class="tone {e.status}" aria-hidden="true"></span>
          <span class="t">{relTime(e.atMs)}</span>
          <span class="op">{e.op || "—"}</span>
          <span class="st {e.status}">{e.status === "failure" ? "blocked" : "ok"}</span>
          {#if e.reason}<span class="reason" title={e.reason}>{e.reason}</span>{/if}
          {#if e.profileId}<span class="prof" title="profile">{e.profileId}</span>{/if}
        </li>
      {/each}
    </ul>
  {/if}
</div>

<style>
  .activity { display: flex; flex-direction: column; gap: var(--space-4); max-width: 1100px; font-family: var(--type-font); }
  header { display: flex; align-items: flex-start; justify-content: space-between; gap: var(--space-4); flex-wrap: wrap; }
  .title { display: flex; flex-direction: column; gap: var(--space-1); min-width: 0; }
  h1 { margin: 0; font-size: 22px; font-weight: 600; letter-spacing: -0.02em; color: var(--text-primary); }
  .lede { margin: 0; font-size: 13.5px; color: var(--text-secondary); line-height: 1.55; max-width: 660px; }
  .tools { display: flex; align-items: center; gap: var(--space-2); flex-shrink: 0; }
  .muted { font-size: 13px; color: var(--text-tertiary); }
  .err { margin: 0; font-size: 12.5px; color: var(--error); font-family: var(--type-mono); }

  .badge { appearance: none; display: inline-flex; align-items: center; gap: 6px; font: inherit; font-size: 11.5px; font-weight: 500; padding: 5px var(--space-2); border-radius: var(--radius-pill); border: 0.5px solid var(--border); background: var(--bg-hover); color: var(--text-secondary); cursor: pointer; white-space: nowrap; transition: border-color var(--ease-fast), background var(--ease-fast); }
  .badge:hover { border-color: var(--border-focus); }
  .badge .dot { width: 7px; height: 7px; border-radius: 50%; background: var(--text-tertiary); }
  .badge.intact { color: var(--success); background: color-mix(in srgb, var(--success) 12%, transparent); border-color: color-mix(in srgb, var(--success) 30%, transparent); }
  .badge.intact .dot { background: var(--success); }
  .badge.tampered, .badge.error { color: var(--error); background: color-mix(in srgb, var(--error) 12%, transparent); border-color: color-mix(in srgb, var(--error) 30%, transparent); }
  .badge.tampered .dot, .badge.error .dot { background: var(--error); }
  .badge.verifying .dot { background: var(--warning); }

  .seg { display: inline-flex; border: 0.5px solid var(--border); border-radius: var(--radius-md); overflow: hidden; }
  .seg button { appearance: none; background: transparent; border: 0; padding: 6px var(--space-3); font: inherit; font-size: 12.5px; color: var(--text-secondary); cursor: pointer; transition: background var(--ease-fast); }
  .seg button.on { background: var(--bg-active); color: var(--text-primary); }
  .seg button:not(.on):hover { background: var(--bg-hover); }

  .trail {
    list-style: none; margin: 0; padding: var(--space-2) var(--space-4);
    background: var(--surface-sunken); border: 0.5px solid var(--border);
    border-radius: var(--radius-lg, 14px);
    display: flex; flex-direction: column;
    font-family: var(--type-mono); font-size: 12px;
  }
  .line { display: flex; align-items: center; gap: var(--space-2); padding: 6px 0; border-bottom: 0.5px solid var(--border-subtle); }
  .line:last-child { border-bottom: 0; }
  .tone { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; background: var(--success); }
  .tone.failure { background: var(--error); }
  .t { color: var(--text-ghost); flex-shrink: 0; min-width: 56px; }
  .op { color: var(--text-primary); flex-shrink: 0; }
  .st { flex-shrink: 0; font-size: 10.5px; padding: 1px 6px; border-radius: var(--radius-pill); text-transform: uppercase; letter-spacing: 0.04em; color: var(--success); background: color-mix(in srgb, var(--success) 14%, transparent); }
  .st.failure { color: var(--error); background: color-mix(in srgb, var(--error) 14%, transparent); }
  .reason { color: var(--text-secondary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .prof { margin-left: auto; color: var(--text-tertiary); flex-shrink: 0; padding-left: var(--space-3); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 220px; }
</style>
